import { MultiLingual } from '@/models/language.model';
import { PaginationFilterRequest } from './core.model';
import { utcToJSDate } from '@/utils/date-time';

/** Hubs are in Hong Kong: slots, receipt times and Hub Run times are shown in HK time. */
export const energyHubTimeZone = 'Asia/Hong_Kong';

export enum EnergyHubStatus {
  Draft = 1,
  /** Listed in the app and accepts check-ins. */
  Active = 2,
  Paused = 3,
  Closed = 4,
}
export const EnergyHubStatusList = [
  { value: 1, label: 'Draft' },
  { value: 2, label: 'Active' },
  { value: 3, label: 'Paused' },
  { value: 4, label: 'Closed' },
];

export enum EnergyHubCategory {
  Cafe = 1,
  Restaurant = 2,
  Bar = 3,
  Gym = 4,
  YogaPilates = 5,
  MassageRecovery = 6,
  JuiceBar = 7,
  Other = 99,
}
export const EnergyHubCategoryList = [
  { value: 1, label: 'Cafe' },
  { value: 2, label: 'Restaurant' },
  { value: 3, label: 'Bar' },
  { value: 4, label: 'Gym' },
  { value: 5, label: 'Yoga / Pilates' },
  { value: 6, label: 'Massage / Recovery' },
  { value: 7, label: 'Juice Bar' },
  { value: 99, label: 'Other' },
];

export enum EnergyHubOfferType {
  FreeExtra = 1,
  FreeUpgrade = 2,
  Combo = 3,
  /** The only type that takes discountPercent. */
  Discount = 4,
}
export const EnergyHubOfferTypeList = [
  { value: 1, label: 'Free Extra' },
  { value: 2, label: 'Free Upgrade' },
  { value: 3, label: 'Combo' },
  { value: 4, label: 'Discount' },
];

export enum EnergyHubClubVisitStatus {
  Requested = 1,
  Approved = 2,
  Rejected = 3,
  Cancelled = 4,
}
export const EnergyHubClubVisitStatusList = [
  { value: 1, label: 'Requested' },
  { value: 2, label: 'Approved' },
  { value: 3, label: 'Rejected' },
  { value: 4, label: 'Cancelled' },
];

export enum EnergyHubReceiptStatus {
  Pending = 1,
  Approved = 2,
  Rejected = 3,
}
export const EnergyHubReceiptStatusList = [
  { value: 1, label: 'Pending' },
  { value: 2, label: 'Approved' },
  { value: 3, label: 'Rejected' },
];

export enum EnergyHubReceiptMatchSource {
  None = 0,
  Runner = 1,
  Staff = 2,
}
export const EnergyHubReceiptMatchSourceList = [
  { value: 0, label: 'Solo' },
  { value: 1, label: 'Runner picked' },
  { value: 2, label: 'Staff linked' },
];

export enum EnergyHubCreditTransactionType {
  TopUp = 1,
  CheckInFee = 2,
  PurchaseFee = 3,
  Refund = 4,
  Adjustment = 5,
}
export const EnergyHubCreditTransactionTypeList = [
  { value: 1, label: 'Top-up' },
  { value: 2, label: 'Check-in fee' },
  { value: 3, label: 'Purchase fee' },
  { value: 4, label: 'Refund' },
  { value: 5, label: 'Adjustment' },
];

/** .NET DayOfWeek: Sunday = 0. */
export const DayOfWeekList = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

export const formatHkd = (value?: number) =>
  value == null ? '-' : `HK$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// ---------- Hubs ----------

export interface EnergyHubList {
  id: number;
  uuid: string;
  name: string;
  category: EnergyHubCategory;
  status: EnergyHubStatus;
  isFoundingHub: boolean;
  freeUntil?: Date;
  creditBalance: number;
  createdAt: Date;
}

export interface EnergyHubCreditSummary {
  balance: number;
  totalTopUp: number;
  totalFees: number;
  totalWaived: number;
  totalRefunded: number;
  totalAdjusted: number;
}

/**
 * Text fields hold every language (language code → text). The app never sees this: the server
 * returns one language, picked from the Elites-User-Language header (English by default).
 */
export interface EnergyHub extends Omit<EnergyHubList, 'name'> {
  name: MultiLingual;
  description?: MultiLingual | null;
  address?: MultiLingual | null;
  lat: number;
  lng: number;
  cityId?: number;
  logoUrl?: string;
  coverImageUrl?: string;
  openingHours?: string;
  checkInFee: number;
  purchaseFee: number;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  adminNote?: string;
  credit: EnergyHubCreditSummary;
  offers: EnergyHubOffer[];
  hostingSlots: EnergyHubHostingSlot[];
  checkInTags: EnergyHubCheckInTag[];
}

export interface UpsertEnergyHub {
  id?: number;
  name: MultiLingual;
  description?: MultiLingual;
  category?: EnergyHubCategory;
  address?: MultiLingual;
  lat?: number;
  lng?: number;
  cityId?: number;
  logoUrl?: string;
  coverImageUrl?: string;
  openingHours?: string;
  status: EnergyHubStatus;
  isFoundingHub: boolean;
  freeUntil?: Date;
  checkInFee?: number;
  purchaseFee?: number;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  adminNote?: string;
}

export class EnergyHubPaginationFilter extends PaginationFilterRequest {
  status?: EnergyHubStatus;
  category?: EnergyHubCategory;
}

export function energyHubListFromJson(json: any): EnergyHubList {
  return {
    ...json,
    freeUntil: utcToJSDate(json.freeUntil),
    createdAt: utcToJSDate(json.createdAt),
  } as EnergyHubList;
}

export function energyHubFromJson(json: any): EnergyHub {
  return {
    ...energyHubListFromJson(json),
    offers: (json.offers ?? []).map(energyHubOfferFromJson),
    hostingSlots: json.hostingSlots ?? [],
    checkInTags: (json.checkInTags ?? []).map((t: any) => ({ ...t, createdAt: utcToJSDate(t.createdAt) })),
  } as EnergyHub;
}

// ---------- Offers / slots / tags ----------

export interface EnergyHubOffer {
  id: number;
  type: EnergyHubOfferType;
  title: MultiLingual;
  terms?: MultiLingual | null;
  discountPercent?: number;
  isActive: boolean;
  validFrom?: Date;
  validTo?: Date;
}

export interface UpsertEnergyHubOffer {
  type: EnergyHubOfferType;
  title: MultiLingual;
  terms?: MultiLingual;
  discountPercent?: number;
  isActive: boolean;
  validFrom?: Date;
  validTo?: Date;
}

export function energyHubOfferFromJson(json: any): EnergyHubOffer {
  return { ...json, validFrom: utcToJSDate(json.validFrom), validTo: utcToJSDate(json.validTo) } as EnergyHubOffer;
}

export interface EnergyHubHostingSlot {
  dayOfWeek: number;
  /** "HH:mm:ss", HK local time. */
  startTime: string;
  endTime: string;
  maxCustomers?: number;
  note?: string;
}

export interface EnergyHubCheckInTag {
  id: number;
  code: string;
  label?: string;
  isActive: boolean;
  /** Print as a QR code / write to the NFC tag. */
  checkInUrl: string;
  createdAt: Date;
}

// ---------- Club visits (Hub Runs) ----------

export interface EnergyHubClubVisit {
  id: number;
  energyHubId: number;
  energyHubName: string;
  runningClubEventId: number;
  eventName: string;
  runningClubId: number;
  clubName?: string;
  startTime: Date;
  endTime?: Date;
  status: EnergyHubClubVisitStatus;
  decidedAt?: Date;
  note?: string;
  checkInCount: number;
  /** When the club requested it, or staff added it. */
  createdAt: Date;
}

export interface UpsertEnergyHubClubVisit {
  runningClubEventId?: number;
  status: EnergyHubClubVisitStatus;
  note?: string;
}

export class EnergyHubClubVisitPaginationFilter extends PaginationFilterRequest {
  status?: EnergyHubClubVisitStatus;
  upcomingOnly?: boolean;
}

export function energyHubClubVisitFromJson(json: any): EnergyHubClubVisit {
  return {
    ...json,
    startTime: utcToJSDate(json.startTime),
    endTime: utcToJSDate(json.endTime),
    decidedAt: utcToJSDate(json.decidedAt),
    createdAt: utcToJSDate(json.createdAt),
  } as EnergyHubClubVisit;
}

// ---------- Visit log ----------

export interface EnergyHubVisit {
  id: number;
  userId: number;
  userName?: string;
  checkedInAt: Date;
  clubVisitId?: number;
  eventName?: string;
  clubName?: string;
  fee: number;
  waivedFee: number;
}

export class EnergyHubVisitPaginationFilter extends PaginationFilterRequest {
  from?: Date;
  to?: Date;
  clubOnly?: boolean;
}

export function energyHubVisitFromJson(json: any): EnergyHubVisit {
  return { ...json, checkedInAt: utcToJSDate(json.checkedInAt) } as EnergyHubVisit;
}

// ---------- Receipts ----------

export interface EnergyHubReceipt {
  id: number;
  energyHubId: number;
  hubName: string;
  userId: number;
  userName?: string;
  imageUrl: string;
  submittedAt: Date;
  receiptTime?: Date;
  amount?: number;
  clubVisitId?: number;
  clubEventName?: string;
  matchSource: EnergyHubReceiptMatchSource;
  status: EnergyHubReceiptStatus;
  reviewedAt?: Date;
  reviewedByUserId?: number;
  rejectReason?: string;
}

export class EnergyHubReceiptPaginationFilter extends PaginationFilterRequest {
  status?: EnergyHubReceiptStatus;
  energyHubId?: number;
  userId?: number;
}

export function energyHubReceiptFromJson(json: any): EnergyHubReceipt {
  return {
    ...json,
    submittedAt: utcToJSDate(json.submittedAt),
    receiptTime: utcToJSDate(json.receiptTime),
    reviewedAt: utcToJSDate(json.reviewedAt),
  } as EnergyHubReceipt;
}

export interface EnergyHubClubRunCandidate {
  clubVisitId: number;
  eventName: string;
  clubName?: string;
  startTime: Date;
  endTime?: Date;
  checkedInAtHub: boolean;
  /** Minutes between the receipt time and the run's check-in window; 0 = inside it. */
  gapMinutes: number;
  isSuggested: boolean;
}

export function energyHubClubRunCandidateFromJson(json: any): EnergyHubClubRunCandidate {
  return { ...json, startTime: utcToJSDate(json.startTime), endTime: utcToJSDate(json.endTime) } as EnergyHubClubRunCandidate;
}

export interface EnergyHubReceiptApproveRequest {
  /** null = solo purchase. */
  clubVisitId?: number | null;
  receiptTime?: Date;
  amount?: number;
}

// ---------- Credit ----------

export interface EnergyHubCreditTransaction {
  id: number;
  type: EnergyHubCreditTransactionType;
  /** Signed HK$: top-ups positive, fees and refunds negative. */
  amount: number;
  waivedAmount: number;
  visitId?: number;
  receiptId?: number;
  note?: string;
  createdBy: number;
  createdAt: Date;
}

export function energyHubCreditTransactionFromJson(json: any): EnergyHubCreditTransaction {
  return { ...json, createdAt: utcToJSDate(json.createdAt) } as EnergyHubCreditTransaction;
}

export interface EnergyHubCreditRequest {
  amount?: number;
  note?: string;
  idempotencyKey?: string;
}
